import React, { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { FiCode, FiEye } from "react-icons/fi";
import { sanitizeBlogHtml } from "../../utils/blogHtml";
import styles from "./CustomQuillEditor.module.css";

const FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "blockquote",
  "code-block",
  "link",
  "image",
  "table",
  "table-row",
  "table-body",
  "table-container",
];

const isEqualHtml = (left = "", right = "") =>
  String(left || "").trim() === String(right || "").trim();

const imageMimeToExtension = (mime = "") => {
  switch (String(mime).toLowerCase()) {
    case "image/jpeg":
    case "image/jpg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    case "image/gif":
      return "gif";
    case "image/bmp":
      return "bmp";
    case "image/svg+xml":
      return "svg";
    default:
      return "png";
  }
};

const dataUrlToFile = async (dataUrl, fallbackName = "pasted-image") => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const ext = imageMimeToExtension(blob.type);
  const safeName = fallbackName.replace(/\.[^.]+$/, "");
  return new File([blob], `${safeName}.${ext}`, {
    type: blob.type || "image/png",
  });
};

export default function CustomQuillEditor({
  value = "",
  onChange,
  placeholder = "Write something...",
  onImageUpload,
}) {
  const editorRef = useRef(null);
  const toolbarRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const onImageUploadRef = useRef(onImageUpload);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onImageUploadRef.current = onImageUpload;
  }, [onImageUpload]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!editorRef.current || !toolbarRef.current || quillRef.current) return;

    const editorNode = editorRef.current;
    const quill = new Quill(editorNode, {
      theme: "snow",
      placeholder,
      modules: {
        table: true,
        toolbar: {
          container: toolbarRef.current,
        },
      },
      formats: FORMATS,
    });

    quillRef.current = quill;

    const openImagePicker = async () => {
      if (!onImageUploadRef.current) {
        const url = window.prompt("Image URL");
        if (!url) return;
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, "image", url, "user");
        quill.setSelection(range.index + 1, 0, "silent");
        return;
      }

      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;

        const range = quill.getSelection(true);
        try {
          const result = await onImageUploadRef.current(file);
          const imageUrl = result?.url || result?.path || result;
          if (!imageUrl) return;
          quill.insertEmbed(range.index, "image", imageUrl, "user");
          quill.setSelection(range.index + 1, 0, "silent");
        } catch (error) {
          console.error("Image upload failed", error);
        }
      };
      input.click();
    };

    quill.getModule("toolbar")?.addHandler("image", openImagePicker);

    const uploadPastedImage = async (src, index) => {
      if (!onImageUploadRef.current) return src;

      const file = await dataUrlToFile(src, `pasted-image-${index + 1}`);
      const result = await onImageUploadRef.current(file);
      return result?.url || result?.path || result || src;
    };

    const handlePaste = async (event) => {
      const clipboardData = event.clipboardData;
      const html = clipboardData?.getData("text/html");
      const text = clipboardData?.getData("text/plain") || "";
      const imageFiles = Array.from(clipboardData?.files || []).filter((file) =>
        file.type.startsWith("image/"),
      );

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const range = quill.getSelection(true);
      const insertAt = range.index;

      if (html) {
        try {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const pastedImages = Array.from(doc.querySelectorAll("img")).filter(
            (img) => /^data:image\//i.test(img.getAttribute("src") || ""),
          );

          if (pastedImages.length && onImageUploadRef.current) {
            for (let index = 0; index < pastedImages.length; index += 1) {
              const img = pastedImages[index];
              const src = img.getAttribute("src") || "";
              const uploadedUrl = await uploadPastedImage(src, index);
              if (uploadedUrl) {
                img.setAttribute("src", uploadedUrl);
              }
            }
          }

          const cleanedHtml = sanitizeBlogHtml(doc.body.innerHTML);
          if (cleanedHtml) {
            quill.clipboard.dangerouslyPasteHTML(insertAt, cleanedHtml, "user");
            return;
          }
        } catch (error) {
          console.error("HTML paste failed", error);
        }
      }

      if (imageFiles.length && onImageUploadRef.current) {
        let offset = 0;
        for (const file of imageFiles) {
          try {
            const result = await onImageUploadRef.current(file);
            const imageUrl = result?.url || result?.path || result;
            if (!imageUrl) continue;
            quill.insertEmbed(insertAt + offset, "image", imageUrl, "user");
            offset += 1;
          } catch (error) {
            console.error("Pasted image upload failed", error);
          }
        }
      }

      if (!html && text) {
        quill.insertText(insertAt, text, "user");
        quill.setSelection(insertAt + text.length, 0, "silent");
      }
    };

    const emitChange = () => {
      const html = quill.root.innerHTML === "<p><br></p>" ? "" : quill.root.innerHTML;
      onChangeRef.current?.(html);
    };

    quill.on("text-change", emitChange);
    quill.root.addEventListener("paste", handlePaste, true);

    if (valueRef.current) {
      quill.clipboard.dangerouslyPasteHTML(valueRef.current, "silent");
    }

    return () => {
      quill.off("text-change", emitChange);
      quill.root.removeEventListener("paste", handlePaste, true);
      quillRef.current = null;
      // React StrictMode mounts this effect, cleans it up, then mounts it
      // again in development. Quill converts editorRef's plain <div> into a
      // .ql-container/.ql-editor structure on construction, so re-running
      // `new Quill(editorRef.current, ...)` on that already-transformed node
      // (instead of a clean one) corrupts the initial content. Resetting the
      // container here means the second mount initializes Quill on a clean
      // <div> again, just like the first one did.
      editorNode.innerHTML = "";
    };
  }, [placeholder]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    const nextValue = String(value || "");
    const currentValue = quill.root.innerHTML === "<p><br></p>" ? "" : quill.root.innerHTML;

    if (isEqualHtml(nextValue, currentValue)) {
      return;
    }

    const selection = quill.getSelection();
    if (!nextValue) {
      quill.setText("");
    } else {
      quill.clipboard.dangerouslyPasteHTML(nextValue, "silent");
    }
    if (selection) {
      quill.setSelection(Math.min(selection.index, quill.getLength() - 1), selection.length, "silent");
    }
  }, [value]);

  return (
    <div className={styles.shell}>
      <div ref={toolbarRef} className={`ql-toolbar ql-snow ${styles.toolbar}`}>
        <span className="ql-formats">
          <select className="ql-header" defaultValue="">
            <option value="">Paragraph / Span</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
            <option value="4">Heading 4</option>
            <option value="5">Heading 5</option>
            <option value="6">Heading 6</option>
          </select>
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-bold" />
          <button type="button" className="ql-italic" />
          <button type="button" className="ql-underline" />
          <button type="button" className="ql-strike" />
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-list" value="ordered" />
          <button type="button" className="ql-list" value="bullet" />
          <button type="button" className="ql-blockquote" />
          <button type="button" className="ql-code-block" />
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-link" />
          <button type="button" className="ql-image" />
          <button type="button" className="ql-clean" />
        </span>
      </div>

      <div className={styles.editorFrame}>
        <div ref={editorRef} className={styles.editor} />
        <div className={styles.overlayHint}>
          <span className={styles.overlayIcon}>
            <FiEye />
          </span>
          <span>Visual editor</span>
          <span className={styles.overlaySlash}>|</span>
          <span className={styles.overlayIcon}>
            <FiCode />
          </span>
          <span>Code ready</span>
        </div>
      </div>
    </div>
  );
}
